import React, { useRef, useState } from "react";
import { useLocation } from "wouter";
import { ChevronLeft } from "lucide-react";
import NeedHelp from "@/components/NeedHelp";

const RegistrationCodeInput: React.FC = () => {
  const [location, setLocation] = useLocation();
  const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const handleChange = (index: number, value: string) => {
    if (!/^[A-Za-z0-9]*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.toUpperCase();
    setCode(newCode);

    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent) => {
    if (event.key === "Backspace" && !code[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    setLoading(true);
    setError(null);
    const enteredCode = code.join("");

    try {
      const response = await fetch("/fmlm/user/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ registrationCode: enteredCode }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || "Incorrect registration code.");
        setCode(["", "", "", "", "", ""]);
        inputsRef.current[0]?.focus();
        return;
      }

      // Save verified user and registration token in sessionStorage
      sessionStorage.setItem("verifiedUser", JSON.stringify(data.user));
      sessionStorage.setItem("registrationToken", data.registrationToken);

      setLocation("/register"); 
    } catch (err) {
      setError("Something went wrong. Please try again.");
      setCode(["", "", "", "", "", ""]);
      inputsRef.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };
  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasteData = event.clipboardData.getData("text").toUpperCase();

    if (!/^[A-Za-z0-9]*$/.test(pasteData)) return; 

    const pasteArray = pasteData.split("").slice(0, 6); 
    const newCode = [...code];

    pasteArray.forEach((char, i) => {
      newCode[i] = char;
    });

    setCode(newCode);

    // Move focus to the last filled input
    const lastIndex = pasteArray.length - 1;
    if (inputsRef.current[lastIndex]) {
      inputsRef.current[lastIndex]?.focus();
    }
  };


  const isCodeComplete = code.every((c) => c !== "");

  return (
    <div className="min-h-screen flex flex-col items-center justify-start bg-gray-50 px-4 pt-8">
      <button
        onClick={() => setLocation("/landing-page")}
        className="absolute items-center flex top-4 left-4 text-[#4c7375] font-semibold hover:underline"
      >
        <ChevronLeft className="h-5 w-5 mr-2" />
        <span className="text-lg text-[#4c7375] font-medium">Back</span>
      </button>

      <div className="px-3">
        <h1 className="text-2xl text-[#4c7375] font-bold mt-[17rem] mb-4 text-center">
          Enter the code you received in your email
        </h1>
        <p className="text-center text-[#1e1e1e] mb-6">
          Please enter the 6-character registration code
        </p>

        <div className="flex gap-2 justify-center mb-4">
          {code.map((value, index) => (
            <input
              key={index}
              type="text"
              maxLength={1}
              value={value}
              onPaste={handlePaste}
              ref={(el) => (inputsRef.current[index] = el)}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className="w-12 h-12 text-center border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring focus:ring-blue-200 text-xl sm:w-14 sm:h-14"
            />
          ))}
        </div>

        {error && <p className="text-red-500 text-center mb-4">{error}</p>}

        <button
          onClick={handleVerify}
          disabled={!isCodeComplete || loading}
          className={`w-full bg-[#4c7375] text-white py-3 rounded-lg transition-colors ${
            !isCodeComplete || loading
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-[#4c7375]"
          }`}
        >
          {loading ? "Verifying..." : "Verify"}
        </button>
      </div>
      <NeedHelp />
    </div>
  );
};

export default RegistrationCodeInput;
